import React from 'react';
import {useTranslation} from 'react-i18next';
import {device} from 'styled/device';
import {useLocation} from 'react-router-dom';
import {useAppSelector, useIVComputedDetails, useScreenResize} from '../../../hooks';
import {getDocumentType} from '../../../store/slices/identityVerification';
import {IVSteps} from 'utils/types';
import {PATHS} from 'Routes';
import {checkIsDocTypeWithDuplexScan} from 'utils/docTypes';
import backBlueArrow from 'assets/icons/back-blue-icon.svg';
import {SkipIVModal, SkipIVModalRef} from '../SkipIVModal/SkipIVModal';
import {
  BarLine,
  BarLineContainer,
  SelectDocumentText,
  SkipText,
  StepperHeader,
  StepsText,
} from './styled';

export type IVStepperProps = {
  onClickBackMobile?: () => void;
};

// const validDocumentTypes = [...Object.values(PASSPORTS), ...Object.values(DRIVING_LICENSES)];

function IVStepper({onClickBackMobile}: IVStepperProps) {
  const {t} = useTranslation();
  const [step, setStep] = React.useState(1);
  const [steps, setSteps] = React.useState(0);
  const {isMatch: isMobileMode} = useScreenResize(device.mobileXL);
  const location = useLocation();
  const {required, biomatch, ocr} = useIVComputedDetails();

  const documentType = useAppSelector(getDocumentType);
  const nationality = useAppSelector(
    state => state.identityVerification.setupFormDate.nationality,
  );

  const [currentStep, setCurrentStep] = React.useState(IVSteps.select);

  React.useEffect(() => {
    const isSelfie = location.pathname.includes('selfie');
    const isFront = location.pathname.includes('front');
    const isBack = location.pathname.includes('back');
    if (isFront || isBack) {
      setCurrentStep(IVSteps.upload);
    } else if (isSelfie) {
      setCurrentStep(IVSteps.selfie);
    } else {
      setCurrentStep(IVSteps.select);
    }
  }, [location]);

  const isFrontSide = React.useMemo(() => {
    return location.pathname.includes(PATHS.verification.front);
  }, [location.pathname]);

  const isBackSide = React.useMemo(() => {
    return location.pathname.includes(PATHS.verification.back);
  }, [location.pathname]);

  const isDocumentSelection = React.useMemo(() => {
    return location.pathname.includes(PATHS.verification.documentSelection);
  }, [location.pathname]);

  const isSelfie = React.useMemo(() => {
    return location.pathname.includes(PATHS.verification.selfie);
  }, [location.pathname]);

  React.useEffect(() => {
    const step = isFrontSide ? 1 : isBackSide ? 2 : isSelfie ? steps : 0;
    setStep(step);
  }, [isFrontSide, isBackSide, setStep, isSelfie, steps]);

  const getSteps = React.useCallback(
    (documentType?: string) => {
      // const isPassportOrDL = documentType && validDocumentTypes.includes(documentType);
      const isTwoSidesDocument = checkIsDocTypeWithDuplexScan({
        docType: documentType as string,
        nationality: nationality,
      });

      if (!isTwoSidesDocument) {
        if (ocr) {
          return 1;
        }
        if (biomatch) {
          return 2;
        }
      }

      if (isTwoSidesDocument) {
        if (ocr) {
          return 2;
        }
        if (biomatch) {
          return 3;
        }
      }
      return 2;
    },
    [ocr, biomatch, nationality],
  );

  React.useEffect(() => {
    const stepsBasedDocumentType = getSteps(documentType?.value);
    setSteps(stepsBasedDocumentType);
  }, [setSteps, biomatch, documentType, getSteps]);

  const skipIVModalRef = React.useRef<SkipIVModalRef>(null);

  const currentStepText = React.useMemo(() => {
    switch (currentStep) {
      case IVSteps.selfie:
        return t('take_selfie');
      case IVSteps.upload:
        return t('scan_your_document');
      case IVSteps.select:
      default:
        return t('select_your_document');
    }
  }, [t, currentStep]);

  const showSteps = currentStep === IVSteps.upload || currentStep === IVSteps.selfie;
  const openSkipModal = () => skipIVModalRef.current?.open();

  const bar = React.useMemo(() => {
    const width = isMobileMode ? 100 : 520;
    const unit = isMobileMode ? '%' : 'px';
    const height = isMobileMode ? 3 : 8;
    // From 0% to 100%
    const selectedWidth = steps === 0 ? 0 : Number(width) / steps;

    const widthWithUnit = `${width}${unit}`;
    const selectWidthWithUnit = `${selectedWidth * step}${unit}`;
    return (
      <svg width={`${widthWithUnit}`} height={height} fill="none">
        <g width={`${widthWithUnit}`} clipPath="url(#clip0_6787_913)">
          <rect width={`${widthWithUnit}`} height={height} rx="1.5" fill="#B1B5C4" />
          <rect
            width={`${selectWidthWithUnit}`}
            height={height}
            rx="1.5"
            fill="#393ABD"
          />
        </g>
        <defs>
          <clipPath id="clip0_6787_913">
            <rect
              width={`${widthWithUnit}`}
              height={`${height}px`}
              rx="1.5"
              fill="white"
            />
          </clipPath>
        </defs>
      </svg>
    );
  }, [steps, isMobileMode, step]);
  return (
    <>
      <BarLineContainer>
        {showSteps && (
          <StepsText>
            {t('step')} {step} {t('of')} {steps}
          </StepsText>
        )}

        <StepperHeader>
          <SelectDocumentText>
            {isMobileMode && !isDocumentSelection && (
              <img
                src={backBlueArrow}
                alt="back blue arrow"
                onClick={onClickBackMobile}
              />
            )}
            {currentStepText}
          </SelectDocumentText>
          <SkipText onClick={openSkipModal}>{`(${t('skip')})`}</SkipText>
        </StepperHeader>
        <BarLine>{bar}</BarLine>
      </BarLineContainer>
      <SkipIVModal ref={skipIVModalRef} required={required} />
    </>
  );
}

export {IVStepper};
